import { cn } from '@goaltree/ui/lib/utils'
import { ChevronDown, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export function SheetIconButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
    />
  )
}

export function SheetCloseButton({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <SheetIconButton onClick={onClose} aria-label={t('common.close')}>
      <X className="h-4 w-4" />
    </SheetIconButton>
  )
}

// Header shared by the AI and human coach sheets: same surface as the sheet body.
export function CoachSheetHeader({
  avatar,
  name,
  subtitle,
  onIdentityClick,
  identityLabel,
  expanded,
  children,
}: {
  avatar: ReactNode
  name: string
  subtitle: string
  onIdentityClick?: () => void
  identityLabel?: string
  expanded?: boolean
  children: ReactNode
}) {
  const identity = (
    <>
      {avatar}
      <span className="min-w-0">
        <span className="flex items-center gap-1 text-base leading-tight font-extrabold text-foreground">
          <span className="truncate">{name}</span>
          {onIdentityClick && (
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                expanded && 'rotate-180',
              )}
            />
          )}
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{subtitle}</span>
      </span>
    </>
  )
  const identityClass = 'flex min-w-0 flex-1 items-center gap-3 text-left'
  return (
    <div className="relative z-30 flex shrink-0 items-center gap-2 border-b border-border/60 bg-popover px-3.5 py-3">
      {onIdentityClick ? (
        <button
          type="button"
          onClick={onIdentityClick}
          aria-label={identityLabel}
          aria-expanded={expanded}
          className={cn(identityClass, 'cursor-pointer')}
        >
          {identity}
        </button>
      ) : (
        <div className={identityClass}>{identity}</div>
      )}
      {children}
    </div>
  )
}

export function UserBubble({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-[85%] self-end rounded-2xl rounded-tr-sm bg-foreground px-3 py-2">
      <p className="text-sm leading-snug whitespace-pre-wrap break-words text-background">
        {children}
      </p>
    </div>
  )
}
